import { useSyncExternalStore } from 'react';

import { storageSubject, userStore } from '../../../utils/userStorage';
import type { RecordEntry } from '../../records/core/recordEntries';

/*
 * The records that were worked on: written or changed, newest first.
 *
 * The sibling of the bookmarks, and its opposite. A bookmark is set by
 * hand and stays until it is cleared; this list is kept by the app and
 * forgets on its own — it answers "what was I just doing?", not "what
 * do I keep coming back to". Same shape, therefore: area, uuid, the
 * label the record wore, and the way back to it.
 *
 * Held in the browser like the bookmarks (there is no user profile in
 * the backend), but unlike them it is not something anybody set on
 * purpose — so the reset of local view state may take it along.
 */
const STORAGE_KEY = 'recentRecords';

/* short enough to stay a shortcut: a list that has to be read is no
   faster than the area it points into */
const KEPT = 8;

const listeners = new Set<() => void>();
const EMPTY: RecordEntry[] = [];

/* the parsed list, kept because getSnapshot has to hand out the same
   array as long as nothing was written */
let cached: RecordEntry[] | null = null;
/* whose list that is: the browser remembers per logged-in user, so a
   cache held across a change of user would be the wrong one (see
   utils/userStorage) */
let cachedFor: string | null = null;

const isUsable = (entry: unknown): entry is RecordEntry =>
  !!entry &&
  typeof entry === 'object' &&
  typeof (entry as RecordEntry).uuid === 'string' &&
  typeof (entry as RecordEntry).to === 'string' &&
  typeof (entry as RecordEntry).area === 'string';

/* the same identity a bookmark has: one record of one area */
export const recordKey = ({
  area,
  uuid,
}: {
  area: string;
  uuid: string | undefined;
}) => `${area}:${uuid}`;

function read(): RecordEntry[] {
  if (cached && cachedFor === storageSubject()) return cached;
  try {
    const stored: unknown = JSON.parse(
      userStore.getItem(STORAGE_KEY) ?? 'null'
    );
    cached = Array.isArray(stored) ? stored.filter(isUsable) : EMPTY;
  } catch {
    cached = EMPTY;
  }
  cachedFor = storageSubject();
  return cached;
}

function write(next: RecordEntry[]) {
  cached = next;
  cachedFor = storageSubject();
  userStore.setItem(STORAGE_KEY, JSON.stringify(next));
  listeners.forEach((notify) => notify());
}

/*
 * Notes a record as just worked on: it goes to the front, and the same
 * record noted again moves there rather than appearing twice — with
 * whatever it is called now, which is why the entry is replaced and
 * not just moved.
 */
export function rememberRecord(entry: unknown): void {
  if (!isUsable(entry)) return;
  const key = recordKey(entry);
  const others = read().filter((record) => recordKey(record) !== key);
  write([entry, ...others].slice(0, KEPT));
}

export function forgetRecords(): void {
  write(EMPTY);
}

/* One record off the trail. The list forgets on its own, but not fast
   enough for whoever wants a detour gone from their dashboard now. */
export function forgetRecord(entry: {
  area: string;
  uuid: string | undefined;
}): void {
  const key = recordKey(entry);
  write(read().filter((record) => recordKey(record) !== key));
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  /* the app open in a second tab writes the same storage */
  const onStorage = (event: StorageEvent) => {
    if (event.key && event.key !== STORAGE_KEY) return;
    cached = null;
    listener();
  };
  window.addEventListener('storage', onStorage);
  return () => {
    listeners.delete(listener);
    window.removeEventListener('storage', onStorage);
  };
}

export function useRecentRecords(): RecordEntry[] {
  return useSyncExternalStore(subscribe, read, () => EMPTY);
}
